import * as React from 'react';
import { Button } from '@/components/ui/button';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches a render error in one screen instead of losing the whole panel.
 *
 * ⚠️ **Added because the absence of one turned a small bug into a total
 * outage.** `Button` passed two children to a Radix `Slot`, which throws; React
 * unmounted the entire tree, and the panel rendered a blank white page with the
 * cause visible only in the browser console. An admin sees "the tool is
 * broken", nobody can tell which screen did it, and the report that reaches a
 * developer is "white screen after login".
 *
 * A boundary does not make the bug less bad — it makes it *legible*. The
 * failing screen says so, names the error, and the navigation around it keeps
 * working so the rest of the panel is still usable.
 *
 * ⚠️ **The error message is rendered, but nothing else about the state is.**
 * A component that throws while rendering a child profile has that child's data
 * in scope, and a boundary that dumped props "to help debugging" would put it
 * on screen — and into whatever screenshot gets attached to the bug report.
 * Message and stack only.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('Admin panel render error', error, info.componentStack);
  }

  override render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        className="mx-auto max-w-xl rounded-lg border border-danger/30 bg-danger/5 p-6"
      >
        <h2 className="text-lg font-semibold text-ink">This screen failed to render</h2>
        <p className="mt-2 text-sm text-ink-soft">
          The rest of the panel still works — use the navigation to move elsewhere. If this keeps
          happening, send a developer the message below.
        </p>

        <pre className="mt-4 overflow-x-auto rounded bg-surface-sunken p-3 font-mono text-xs text-ink-soft">
          {error.message}
        </pre>

        <Button className="mt-4" variant="secondary" onClick={() => this.setState({ error: null })}>
          Try again
        </Button>
      </div>
    );
  }
}
